import React, { useState, useMemo } from 'react';
import ApplicationDetail from './ApplicationDetail.js';
import { APPLICATION_STATUSES } from '../constants/crmConstants';

/**
 * EvaluateApplication Component
 * Functional workspace for assessing university applications.
 * Features: Multi-stage filtering, analytical submission graph, 
 * and integrated progression pipeline activation.
 */
const EvaluateApplication = ({ 
  onBack, 
  users = [], 
  applications = [], 
  onUpdateApplication, 
  onSaveStudent, 
  currentUserId, 
  settings,
  viewOnly = false 
}) => {
  const [selectedStage, setSelectedStage] = useState('Received');
  const [selectedAppId, setSelectedAppId] = useState(null);
  const [agentFilter, setAgentFilter] = useState('All Agents');
  const [dateFilter, setDateFilter] = useState(''); // NEW: State for the date filter
  const [programFilter, setProgramFilter] = useState('All Programs');
  const [intakeFilter, setIntakeFilter] = useState('All Intakes');


  const [studentFilter, setStudentFilter] = useState('All Students');
  const [isStudentSearchOpen, setIsStudentSearchOpen] = useState(false);
  const [studentSearchQuery, setStudentSearchQuery] = useState('');

    // Derive agent options from global settings object
  const agentOptions = useMemo(() => {
    const agentsFromSetup = (settings?.sourceAgents || []).map(a => 
      typeof a === 'string' ? a : a.name
    );
    return ['All Agents', ...agentsFromSetup];
  }, [settings?.sourceAgents]);

  
  const appOwnerOptions = useMemo(() => {
    return (users || []).filter(u => u.role === 'TWG Apps and Visa');
  }, [users]);

  const [isAgentSearchOpen, setIsAgentSearchOpen] = useState(false);
  const [agentSearchQuery, setAgentSearchQuery] = useState('');

  const filteredAgentOptions = useMemo(() => {
    if (!agentSearchQuery) return agentOptions;
    return agentOptions.filter(agent => 
      agent.toLowerCase().includes(agentSearchQuery.toLowerCase())
    );
  }, [agentOptions, agentSearchQuery]);



  const [enrollModal, setEnrollModal] = useState({ show: false, appId: null, statusData: null });
  const [enrollForm, setEnrollForm] = useState({
    country: '',
    twgProgram: '',
    intake: '',
    universityId: '',
    course: ''
  });

  const uniqueAgents = useMemo(() => {
    const agents = applications
      .map(app => app.partnerName || 'No Partner Linked')
      .filter(Boolean);
    return ['All Agents', ...new Set(agents)];
  }, [applications]);

  /**
   * CORE FILTER LOGIC
   * Combines Agent selection AND Date selection
   */
  const agentFilteredApps = useMemo(() => {
    let list = applications;

    
    if (studentFilter !== 'All Students') {
      return list.filter(app => app.applicantName === studentFilter);
    }

    if (agentFilter !== 'All Agents') {
      list = list.filter(app => (app.partnerName || 'No Partner Linked') === agentFilter);
    }

    // 2. Apply Date Filter (Matching YYYY-MM-DD string)
    if (dateFilter) {
      list = list.filter(app => app.dateApplied === dateFilter);
    }

    if (programFilter !== 'All Programs') {
      list = list.filter(app => {
        // Look at Preference 1 (Index 0) in the programSelections array
        const firstPrefProgram = app.programSelections?.[0]?.twgProgram;
        return firstPrefProgram === programFilter;
      });
    }

    if (intakeFilter !== 'All Intakes') {
      list = list.filter(app => {
        // Check Preference 1 (Index 0) for the intake value
        const firstPrefIntake = app.programSelections?.[0]?.intake;
        return firstPrefIntake === intakeFilter;
      });
    }

    return list;
  }, [applications, agentFilter, dateFilter, programFilter, intakeFilter, studentFilter]);

  const activeStageNames = APPLICATION_STATUSES.filter(
    status => status !== 'Draft' && status !== 'Rejected' && status !== 'Withdrawn' 
    && status !== 'Enrolled in TWG Program' && status !== 'Offer Signed - Pending Payment' 
    && status !== 'Offer Signed - Payment Completed'
  );

  const totalActiveCount = useMemo(() => {
    return agentFilteredApps.filter(app => 
      activeStageNames.includes(app.status)
    ).length;
  }, [agentFilteredApps, activeStageNames]);

  const enrollmentMilestoneStages = [
    "Offer Signed - Pending Payment",
    "Offer Signed - Payment Completed",
    "Enrolled in TWG Program"
  ];

  const enrolledSum = useMemo(() => {
    return agentFilteredApps.filter(app => 
      enrollmentMilestoneStages.includes(app.status)
    ).length;
  }, [agentFilteredApps]);

  // Current view based on selected sidebar stage
  const filteredApps = useMemo(() => {
    return agentFilteredApps.filter(app => app.status === selectedStage);
  }, [agentFilteredApps, selectedStage]);

  const evaluationStages = APPLICATION_STATUSES.map((status, index) => {
    // Define colors for specific stages, fallback to a default slate
    const colorMap = {
      'Draft': 'bg-slate-400',
      'App Submitted': 'bg-blue-500',
      'App Accepted': 'bg-emerald-500',
      'Rejected': 'bg-rose-500',
      'Enrolled in TWG Program': 'bg-purple-500'
    };

    return {
      id: index + 1,
      name: status,
      color: colorMap[status] || 'bg-slate-500'
    };
  });

  const getUniName = (id) => {
    if (!id) return '';
    const uni = (settings?.universities || []).find(u => String(u.id) === String(id));
    return uni ? uni.name : id;
  };

  // Modal Cascading Selection Logic
  const filteredUnisInModal = useMemo(() => {
    if (!enrollForm.country) return [];
    return (settings?.universities || []).filter(u => u.country === enrollForm.country);
  }, [enrollForm.country, settings?.universities]);

  const selectedUniInModal = useMemo(() => {
    return (settings?.universities || []).find(u => String(u.id) === String(enrollForm.universityId));
  }, [enrollForm.universityId, settings?.universities]);

  const applicableTWGProgsInModal = useMemo(() => {
    return selectedUniInModal?.applicablePrograms || [];
  }, [selectedUniInModal]);

  const availableCoursesInModal = useMemo(() => {
  // Return courses specific to the program selected in the modal form
    return selectedUniInModal?.courses?.[enrollForm.twgProgram] || [];
  }, [selectedUniInModal, enrollForm.twgProgram]); // Added twgProgram to dependency array
  
  const availableIntakesInModal = useMemo(() => {
    if (!enrollForm.twgProgram) return [];
    return (settings?.intakeMappings?.[enrollForm.twgProgram] || []);
  }, [enrollForm.twgProgram, settings?.intakeMappings]);

  // Analytical Submission Chart Logic
  const chartData = useMemo(() => {
    const last8Weeks = [];
    const now = new Date();
    for (let i = 7; i >= 0; i--) {
      const d = new Date();
      d.setDate(now.getDate() - (i * 7));
      const day = d.getDay();
      const diff = d.getDate() - day;
      const startOfWeek = new Date(d.setDate(diff));
      last8Weeks.push({
        label: startOfWeek.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        timestamp: startOfWeek.getTime(),
        count: 0
      });
    }

    agentFilteredApps.forEach(app => {
      if (!app.dateApplied) return;
      const appDate = new Date(app.dateApplied).getTime();
      for (let i = 0; i < last8Weeks.length; i++) {
        const weekStart = last8Weeks[i].timestamp;
        const nextWeekStart = i < 7 ? last8Weeks[i+1].timestamp : Infinity;
        if (appDate >= weekStart && appDate < nextWeekStart) {
          last8Weeks[i].count++;
          break;
        }
      }
    });
    return last8Weeks;
  }, [agentFilteredApps]);

  const maxVal = Math.max(...chartData.map(d => d.count), 5);
  const chartHeight = 80;
  const chartWidth = 1000;

  // Handlers
  const handleUpdate = (appId, data) => {
    if (viewOnly) return;
    if (data.status === 'Enrolled in TWG Program') {
      const app = applications.find(a => a.id === appId);
      if (app) {
        const firstPref = app.programSelections?.[0] || {};
        setEnrollForm({
          country: firstPref.country || '',
          universityId: firstPref.universityId || '',
          twgProgram: firstPref.twgProgram || '',
          course: firstPref.course || '',
          intake: firstPref.intake || ''
        });
        setEnrollModal({ show: true, appId, statusData: data });
      }
    } else {
      if (onUpdateApplication) onUpdateApplication(appId, data);
    }
  };

  const handleFinalEnrollment = async (e) => {
    e.preventDefault();
    if (viewOnly) return;
    const app = applications.find(a => a.id === enrollModal.appId);
    if (!app) return;

    const uniName = getUniName(enrollForm.universityId);
    const intakeParts = (enrollForm.intake || "").split(" ");
    const inheritedMonth = intakeParts[0] || "";
    const inheritedYear = intakeParts[1] ? parseInt(intakeParts[1]) : "";

    const template = settings?.documentTemplates?.[uniName] || 
                     settings?.documentTemplates?.[enrollForm.country] || [];
    const inheritedChecklist = {};
    if (Array.isArray(template)) {
      template.forEach(docName => {
        if (docName) inheritedChecklist[docName] = { status: 'Pending', notes: '', shared: false };
      });
    }

    if (onSaveStudent) {
      await onSaveStudent({
        name: `${app.firstName} ${app.lastName}`.trim(),
        firstName: app.firstName,
        lastName: app.lastName,
        email: app.email,
        phone: app.phone,
        nationality: app.nationality || '',
        stage: 'TWG Program Enrolled', 
        twgProgram: enrollForm.twgProgram,
        commencingMonth: inheritedMonth,
        commencingYear: inheritedYear,
        targetProgram: enrollForm.course,
        expectedProgressionMonth: '',
        expectedProgressionYear: '',
        destinationUniversity: uniName,
        targetCountry: enrollForm.country,
        applicationId: app.id,
        applicantId: app.applicantId,
        enrolledAt: new Date().toISOString(),
        sourceAgent: app.agent || 'Direct',
        documentChecklist: inheritedChecklist,
        academicDocumentsUrls: app.academicDocumentsUrls || [], 
        englishTestResultUrls: app.englishTestResultUrls || [],
        relevantFinancialDocsUrls: app.relevantFinancialDocsUrls || [],
        section8PersonalDocsUrls: app.section8PersonalDocsUrls || [],
        section8AdmissionDocsUrls: app.section8AdmissionDocsUrls || [],
        
        // Inherit evaluation/internal documents
        evaluationDocuments: app.evaluationDocuments || [],
      });
    }
    if (onUpdateApplication) onUpdateApplication(app.id, enrollModal.statusData);
    setEnrollModal({ show: false, appId: null, statusData: null });
  };

  const currentApp = applications.find(a => a.id === selectedAppId);

  return (
    <div className="animate-in fade-in duration-500 pb-20">
      {selectedAppId && currentApp ? (
        <ApplicationDetail 
          application={currentApp} users={users} settings={settings} evaluationStages={evaluationStages}
          onClose={() => setSelectedAppId(null)} onUpdate={handleUpdate} currentUserId={currentUserId} viewOnly={viewOnly}
        />
      ) : (
        <>

          {/* Main Header Container - Vertical Stack */}
          <div className="space-y-8 mb-10">
            {/* TOP ROW: Title & Navigation */}
            <div className="flex items-center gap-4">
              <button 
                onClick={onBack} 
                className="p-2 text-slate-400 hover:text-[#0464AD] hover:bg-slate-100 rounded-xl transition-all active:scale-90"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <div>
                <h2 className="text-3xl font-black text-slate-900 tracking-tighter uppercase leading-none">Evaluate Applications</h2>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Review Queue & Assessment Workflow</p>
              </div>
            </div>

            {/* CONSOLIDATED FILTER PANEL: All filters in one line */}
            <div className="flex flex-wrap items-center gap-4 bg-slate-50/50 p-6 rounded-[2.5rem] border border-slate-100 shadow-sm">
              
              {/* 1. Date Filter */}
              <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-4 py-2 shadow-sm w-52">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest shrink-0">Date:</label>
                <input 
                  type="date"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="text-[10px] font-black uppercase text-[#0464AD] outline-none bg-transparent cursor-pointer w-full"
                />
                {dateFilter && (
                  <button onClick={() => setDateFilter('')} className="text-slate-300 hover:text-red-500">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12"/></svg>
                  </button>
                )}
              </div>

              {/* 2. Program Filter */}
              <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-4 py-2 shadow-sm w-52">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest shrink-0">Program:</label>
                <select 
                  value={programFilter}
                  onChange={(e) => setProgramFilter(e.target.value)}
                  className="text-[10px] font-black uppercase text-[#0464AD] outline-none bg-transparent cursor-pointer w-full truncate"
                >
                  <option value="All Programs">All Programs</option>
                  {(settings?.twgPrograms || []).map(prog => (
                    <option key={prog} value={prog}>{prog}</option>
                  ))}
                </select>
              </div>

              {/* 3. Intake Filter */}
              <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-4 py-2 shadow-sm w-52">
                <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest shrink-0">Intake:</label>
                <select 
                  value={intakeFilter}
                  onChange={(e) => setIntakeFilter(e.target.value)}
                  className="text-[10px] font-black uppercase text-[#0464AD] outline-none bg-transparent cursor-pointer w-full truncate"
                >
                  <option value="All Intakes">All Intakes</option>
                  {[...new Set(applications.map(app => 
                    app.programSelections?.[0]?.intake || app.intakeSummary
                  ).filter(Boolean))].sort().map(intake => (
                    <option key={intake} value={intake}>{intake}</option>
                  ))}
                </select>
              </div>

              {/* 4. Student Filter */}
              <div className="relative">
                <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-4 py-2 shadow-sm w-52">
                  <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest shrink-0">Student:</label>
                  <button 
                    type="button"
                    onClick={() => setIsStudentSearchOpen(!isStudentSearchOpen)}
                    className="text-[10px] font-black uppercase text-[#0464AD] outline-none flex items-center justify-between flex-1 gap-2 overflow-hidden"
                  >
                    <span className="truncate">{studentFilter}</span>
                    <svg className={`w-3 h-3 shrink-0 transition-transform ${isStudentSearchOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>

                {isStudentSearchOpen && (
                  <div className="absolute top-full left-0 mt-2 w-64 bg-white border border-slate-100 shadow-2xl rounded-2xl z-[600] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                    <div className="p-3 border-b border-slate-50 bg-slate-50/50">
                      <input 
                        type="text"
                        autoFocus
                        placeholder="Search students..."
                        className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-[10px] font-bold outline-none focus:ring-2 focus:ring-[#0464AD]"
                        value={studentSearchQuery}
                        onChange={(e) => setStudentSearchQuery(e.target.value)}
                      />
                    </div>
                    <div className="max-h-60 overflow-y-auto">
                      <button
                        onClick={() => { setStudentFilter('All Students'); setIsStudentSearchOpen(false); }}
                        className="w-full text-left px-4 py-2.5 text-[10px] font-black uppercase text-slate-400 hover:bg-slate-50 border-b border-slate-50"
                      >
                        All Students
                      </button>
                      {[...new Set(applications.map(app => app.applicantName))].sort()
                        .filter(name => name.toLowerCase().includes(studentSearchQuery.toLowerCase()))
                        .map(name => (
                          <button
                            key={name}
                            type="button"
                            onClick={() => {
                              setStudentFilter(name);
                              setIsStudentSearchOpen(false);
                              setStudentSearchQuery('');
                            }}
                            className={`w-full text-left px-4 py-2.5 text-[10px] font-black uppercase tracking-tight hover:bg-slate-50 transition-colors ${
                              studentFilter === name ? 'text-[#0464AD] bg-blue-50/50' : 'text-slate-600'
                            }`}
                          >
                            {name}
                          </button>
                        ))}
                    </div>
                  </div>
                )}
              </div>

              {/* 5. Source Agent Filter */}
              <div className="relative">
                <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-4 py-2 shadow-sm w-52">
                  <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest shrink-0">Agent:</label>
                  <button 
                    type="button"
                    onClick={() => setIsAgentSearchOpen(!isAgentSearchOpen)}
                    className="text-[10px] font-black uppercase text-[#0464AD] outline-none flex items-center justify-between flex-1 gap-2 overflow-hidden"
                  >
                    <span className="truncate">{agentFilter}</span>
                    <svg className={`w-3 h-3 shrink-0 transition-transform ${isAgentSearchOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>

                {isAgentSearchOpen && (
                  <div className="absolute top-full left-0 mt-2 w-64 bg-white border border-slate-100 shadow-2xl rounded-2xl z-[600] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                    <div className="p-3 border-b border-slate-50 bg-slate-50/50">
                      <input 
                        type="text"
                        autoFocus
                        placeholder="Search agents..."
                        className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-[10px] font-bold outline-none focus:ring-2 focus:ring-[#0464AD]"
                        value={agentSearchQuery}
                        onChange={(e) => setAgentSearchQuery(e.target.value)}
                      />
                    </div>
                    <div className="max-h-60 overflow-y-auto">
                      <button
                        onClick={() => { setAgentFilter('All Agents'); setIsAgentSearchOpen(false); }}
                        className="w-full text-left px-4 py-2.5 text-[10px] font-black uppercase text-slate-400 hover:bg-slate-50 border-b border-slate-50"
                      >
                        All Agents
                      </button>
                      {filteredAgentOptions.map(agent => (
                        <button
                          key={agent}
                          type="button"
                          onClick={() => {
                            setAgentFilter(agent);
                            setIsAgentSearchOpen(false);
                            setAgentSearchQuery('');
                          }}
                          className={`w-full text-left px-4 py-2.5 text-[10px] font-black uppercase tracking-tight hover:bg-slate-50 transition-colors ${
                            agentFilter === agent ? 'text-[#0464AD] bg-blue-50/50' : 'text-slate-600'
                          }`}
                        >
                          {agent}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* System Badge */}
              {viewOnly && (
                <div className="bg-amber-50 text-amber-600 px-4 py-2 rounded-xl border border-amber-100 text-[10px] font-black uppercase tracking-widest ml-auto">
                  View Only Access
                </div>
              )}
            </div>
          </div>
                    
              

          {/* TOP SECTION: Metrics & Analytics Graph */}
          <div className="grid grid-cols-1 xl:grid-cols-5 gap-8 mb-8">
            
            {/* src/components/EvaluateApplication.js */}

            {/* LEFT COLUMN: Summary Metrics (Aligned with Sidebar) */}
            <div className="xl:col-span-1 space-y-4">
              
              {/* Card 1: Total Active Applications */}
              <div className="bg-white py-4 px-6 rounded-[2rem] border border-slate-100 shadow-sm flex flex-col justify-center">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">
                  Total Active Applications
                </p>
                <div className="flex items-baseline gap-2">
                  <h4 className="text-4xl font-black text-[#0464AD]">{totalActiveCount}</h4>
                </div>
              </div>

              {/* Card 2: Enrolled/Admitted Students */}
              <div className="bg-white py-4 px-6 rounded-[2rem] border border-slate-100 shadow-sm flex flex-col justify-center">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">
                  Enrolled/Admitted Students
                </p>
                <div className="flex items-baseline gap-2">
                  <h4 className="text-4xl font-black text-emerald-500">{enrolledSum}</h4>
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN: Submission History Graph (Aligned with Table) */}
            <div className="xl:col-span-4">
              <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm h-full">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Submissions History</h3>
                </div>
                <div className="relative h-[100px] w-full">
                  <svg viewBox={`0 0 ${chartWidth} ${chartHeight + 20}`} className="w-full h-full overflow-visible">
                    <polyline
                      fill="none"
                      stroke="#0464AD"
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      points={chartData.map((d, i) => `${(chartWidth / (chartData.length - 1)) * i},${chartHeight - (d.count / maxVal) * chartHeight}`).join(' ')}
                    />
                    {chartData.map((d, i) => {
                      const x = (chartWidth / (chartData.length - 1)) * i;
                      const y = chartHeight - (d.count / maxVal) * chartHeight;
                      return (
                        <g key={i}>
                          <circle cx={x} cy={y} r="4" fill="white" stroke="#0464AD" strokeWidth="3" />
                          <text x={x} y={chartHeight + 20} textAnchor="middle" className="text-[9px] font-black fill-slate-300 uppercase tracking-widest">{d.label}</text>
                          {d.count > 0 && <text x={x} y={y - 12} textAnchor="middle" className="text-[10px] font-black fill-[#0464AD]">{d.count}</text>}
                        </g>
                      );
                    })}
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-5 gap-8 items-start">
            {/* Filter Sidebar */}
            <div className="xl:col-span-1">
              <div className="bg-white rounded-[2rem] border border-slate-100 shadow-sm overflow-hidden sticky top-24">
                <div className="divide-y divide-slate-50">
                  {evaluationStages.map((stage) => {
                    const count = agentFilteredApps.filter(a => a.status === stage.name).length;
                    return (
                      <button
                        key={stage.id}
                        onClick={() => setSelectedStage(stage.name)}
                        className={`w-full flex items-center justify-between px-6 py-5 transition-all hover:bg-slate-50 group ${
                          selectedStage === stage.name ? 'bg-[#0464AD]/5' : ''
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full ${stage.color}`}></div>
                          <span className={`text-[10px] font-black text-left leading-tight tracking-wide uppercase transition-colors ${
                            selectedStage === stage.name ? 'text-[#0464AD]' : 'text-slate-400'
                          }`}>
                            {stage.name}
                          </span>
                        </div>
                        <span className={`text-[9px] font-black px-2 py-0.5 rounded-lg ${
                          selectedStage === stage.name ? 'bg-[#0464AD] text-white' : 'bg-slate-100 text-slate-400'
                        }`}>
                          {count}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Queue Table */}
            <div className="xl:col-span-4">
              <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden min-h-[600px]">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse table-auto min-w-[900px]">
                    <thead className="bg-slate-50 border-b border-slate-100">
                      <tr>
                        <th className="px-6 py-5 text-[9px] font-black text-slate-400 uppercase tracking-[0.15em]">Applicant Name</th>
                        <th className="px-6 py-5 text-[9px] font-black text-slate-400 uppercase tracking-[0.15em]">App ID</th>
                        <th className="px-6 py-5 text-[9px] font-black text-slate-400 uppercase tracking-[0.15em]">Date Received</th>
                        <th className="px-6 py-5 text-[9px] font-black text-slate-400 uppercase tracking-[0.15em]">Preferred Program</th>
                        <th className="px-6 py-5 text-[9px] font-black text-slate-400 uppercase tracking-[0.15em]">University</th>
                        <th className="px-6 py-5 text-[9px] font-black text-slate-400 uppercase tracking-[0.15em]">Associated Agent</th>
                        <th className="px-6 py-5"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {filteredApps.length > 0 ? filteredApps.map((app) => (
                        <tr key={app.id} className="hover:bg-slate-50/50 transition-colors group">
                          <td className="px-6 py-6">
                            <span className="text-xs font-bold text-slate-600 tracking-tight uppercase">{app.applicantName}</span>
                          </td>
                          <td className="px-6 py-6">
                            <span className="text-[10px] font-black text-[#0464AD] bg-[#0464AD]/5 border border-[#0464AD]/10 px-2.5 py-1 rounded-lg uppercase tracking-tighter">{app.id}</span>
                          </td>
                          {/* NEW: Date Received Cell */}
                          <td className="px-6 py-6">
                            <span className="text-xs font-bold text-slate-600">
                              {app.dateApplied 
                                ? new Date(app.dateApplied).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) 
                                : app.createdAt 
                                  ? new Date(app.createdAt.seconds * 1000).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                                  : '—'}
                            </span>
                          </td>
                          <td className="px-6 py-6">
                            <div className="flex flex-col">
                              <span className="text-xs font-bold text-slate-700 leading-tight">
                                {app.programSelections?.[0]?.twgProgram || app.programSummary || '—'}
                              </span>
                              <span className="text-[9px] font-black text-slate-400 uppercase mt-0.5">
                                {app.programSelections?.[0]?.intake || app.intakeSummary || '—'}
                              </span>
                            </div>
                          </td>

                          {/* UNIVERSITY COLUMN CELL */}
                          <td className="px-6 py-6">
                            <span className="text-xs font-bold text-slate-800 leading-tight uppercase">
                              {getUniName(app.programSelections?.[0]?.universityId) || '—'}
                            </span>
                          </td>
                          
                          <td className="px-6 py-6">
                            <div className="flex flex-col">
                              {/* Display only the Agent Name (partnerName) */}
                              <span className="text-[10px] font-black text-slate-800 tracking-tight uppercase leading-tight truncate max-w-[140px]">
                                {app.partnerName || 'No Partner Linked'}
                              </span>
                              
                              {/* Display the Agent Code lookup */}
                              {(() => {
                                const agentData = (settings?.sourceAgents || []).find(
                                  a => (typeof a === 'object' && a.name === app.partnerName)
                                );
                                
                                return agentData?.code ? (
                                  <span className="text-[8px] font-black text-[#0464AD] uppercase tracking-wider">
                                    Code: {agentData.code}
                                  </span>
                                ) : null;
                              })()}
                            </div>
                          </td>

                          
                          <td className="px-6 py-6 text-right">
                            <button 
                              className="p-2.5 bg-slate-900 text-white rounded-xl hover:bg-[#0464AD] transition-all shadow-lg active:scale-95 flex items-center justify-center ml-auto group-hover:shadow-blue-200"
                              onClick={() => setSelectedAppId(app.id)}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </button>
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="7" className="px-10 py-32 text-center">
                            <div className="text-5xl mb-6 grayscale opacity-20 filter drop-shadow-sm">📥</div>
                            <h4 className="text-lg font-black text-slate-300 uppercase tracking-tighter mb-1">Queue is empty</h4>
                            <p className="text-xs text-slate-400 font-bold uppercase tracking-widest">No matching assessments found.</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ENROLLMENT MODAL */}
      {enrollModal.show && !viewOnly && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[500] p-4">
          <div className="bg-white rounded-[2.5rem] w-full max-w-xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="px-10 py-8 bg-slate-50 border-b border-slate-100">
              <h3 className="text-2xl font-black text-slate-900 tracking-tighter uppercase">Transition to Progression</h3>
              <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mt-1">Confirm final enrollment details to activate pipeline tracking</p>
            </div>

            <form onSubmit={handleFinalEnrollment} className="p-10 space-y-6">
              <div className="bg-blue-50/50 p-6 rounded-3xl border border-blue-100/50 mb-4">
                <p className="text-[11px] text-[#0464AD] font-medium leading-relaxed">
                  Moving this student to <strong className="uppercase">Enrolled in Hybrid</strong> will create a new entry in the Progression Pipeline section.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-6">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">1. Destination Country</label>
                  <select 
                    className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD] cursor-pointer"
                    value={enrollForm.country}
                    onChange={(e) => setEnrollForm({...enrollForm, country: e.target.value, universityId: '', twgProgram: '', course: '', intake: ''})}
                    required
                  >
                    <option value="">Select Country...</option>
                    {(settings?.countries || []).map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>

                <div className={`space-y-1.5 transition-all ${!enrollForm.country ? 'opacity-30 pointer-events-none grayscale' : ''}`}>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">2. Destination University</label>
                  <select 
                    className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD] cursor-pointer"
                    value={enrollForm.universityId}
                    onChange={(e) => setEnrollForm({...enrollForm, universityId: e.target.value, twgProgram: '', course: '', intake: ''})}
                    required
                  >
                    <option value="">Select University...</option>
                    {filteredUnisInModal.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-6">
                   <div className={`space-y-1.5 transition-all ${!enrollForm.universityId ? 'opacity-30 pointer-events-none grayscale' : ''}`}>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">3. TWG Program</label>
                    <select 
                      className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD] cursor-pointer"
                      value={enrollForm.twgProgram}
                      onChange={(e) => setEnrollForm({...enrollForm, twgProgram: e.target.value, intake: ''})}
                      required
                    >
                      <option value="">Select Program...</option>
                      {applicableTWGProgsInModal.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div className={`space-y-1.5 transition-all ${!enrollForm.universityId ? 'opacity-30 pointer-events-none grayscale' : ''}`}>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">4. Destination Course</label>
                    <select 
                      className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD] cursor-pointer"
                      value={enrollForm.course}
                      onChange={(e) => setEnrollForm({...enrollForm, course: e.target.value})}
                      required
                    >
                      <option value="">Select Course...</option>
                      {availableCoursesInModal.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>

                <div className={`space-y-1.5 transition-all ${!enrollForm.twgProgram ? 'opacity-30 pointer-events-none grayscale' : ''}`}>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">5. TWG Program Commencement (Intake)</label>
                  <select 
                    className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-sm outline-none focus:ring-2 focus:ring-[#0464AD] cursor-pointer"
                    value={enrollForm.intake}
                    onChange={(e) => setEnrollForm({...enrollForm, intake: e.target.value})}
                    required
                  >
                    <option value="">Select Intake...</option>
                    {availableIntakesInModal.map(date => <option key={date} value={date}>{date}</option>)}
                  </select>
                </div>
              </div>

              <div className="pt-6 flex gap-4">
                <button type="button" onClick={() => setEnrollModal({ show: false, appId: null, statusData: null })} className="flex-1 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-slate-800 transition-colors">Cancel Change</button>
                <button type="submit" className="flex-1 bg-[#0464AD] text-white py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl active:scale-95">Confirm & Activate</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EvaluateApplication;